import {WithAPI} from "../../../../api/temperature";
import {Dialog, DialogClose, DialogContent, DialogOptions} from "../../../generic/dialog";
import {ExistingToken, Token} from "../../../../api/admin";
import {useLoader} from "../../../generic/loader";
import {Card} from "../../../generic/card";
import {FormFooter, FormGroup} from "../../../generic/form";
import {Checkbox} from "../../../generic/checkbox";
import {Button} from "../../../generic/button";
import {useFlash} from "../../../generic/flash";

type ApiKeyDialogProps = {
    token?: Token | ExistingToken;
    onSubmit: (token: Token | ExistingToken) => boolean | Promise<boolean>;
    scopes: string[]
} & DialogOptions;

function ApiKeyDialog(props: ApiKeyDialogProps) {
    const token: Token | ExistingToken = props.token ?? {
        name: "",
        ipRegEx: "^.*$",
        scope: ["post_readings"],
        until: null
    }

    const createToken = (form: HTMLFormElement): Token => {
        const fd = new FormData(form);

        const newToken: Token | ExistingToken = {
            name: fd.get("name") as string,
            ipRegEx: fd.get("ipregex") as string,
            scope: fd.getAll("scope") as string[],
            until: token.until
        };

        if (Object.prototype.hasOwnProperty.call(token, "id")) {
            (newToken as ExistingToken).id = (token as ExistingToken).id;
        }

        return newToken;
    }

    return <Dialog {...props}>
        <DialogContent>
            <Card title={token.hasOwnProperty("id") ? `Modify API key ${token.name}` : "Create API Key"}>
                <form onSubmit={(e) => {
                    const token = createToken(e.currentTarget);
                    if (token) {
                        const res = props.onSubmit(token);
                        if (res instanceof Promise) {
                            res.then((res) => {
                                if (res) {
                                    props.onOpenChange(false);
                                }
                            });
                        } else if (res) {
                            props.onOpenChange(false);
                        }
                    }

                    e.preventDefault();
                }}>
                    <FormGroup>
                        <label for="input--api-key-name">Key name:</label>
                        <input type="text" name="name" id="input--api-key-name" value={token.name} required />
                    </FormGroup>

                    <FormGroup>
                        <label for="input--ip-regex">IP Regexp:</label>
                        <input type="text" name="ipregex" id="input--ip-regex" value={token.ipRegEx}/>
                    </FormGroup>

                    <FormGroup>
                        <legend>Scopes:</legend>
                        {props.scopes.map(scope =>
                            <Checkbox name="scope" value={scope} checked={token.scope.indexOf(scope) >= 0}
                                      label={<code>{scope}</code>}/>
                        )}
                    </FormGroup>

                    <FormFooter>
                        <Button type="submit">{token.hasOwnProperty("id") ? "Modify" : "Create"}</Button>
                        <DialogClose>Cancel</DialogClose>
                    </FormFooter>
                </form>
            </Card>
        </DialogContent>
    </Dialog>
}

export function CreateApiKey({api, scopes, ...props}: WithAPI & DialogOptions & { scopes: string[] }) {
    const [showLoader, hideLoader] = useLoader();
    const {success} = useFlash();

    return <ApiKeyDialog scopes={scopes} {...props} onSubmit={async (token) => {
        showLoader("Saving...");
        try {
            const created = await api.admin.createApiKey(token);
            success(<>
                API key {token.name} has been created.<br />
                The key is: <code><strong>{created.tokens[0].key}</strong></code><br />
                You won't be able to recover this key, store it securely.
            </>, {timeout: 0});
        } finally {
            hideLoader();
        }
        return true;
    }}/>;
}

export function ModifyApiKey({api, token, scopes, ...props}: WithAPI & DialogOptions & {
    token: Token,
    scopes: string[]
}) {
    const [showLoader, hideLoader] = useLoader();
    const {success} = useFlash();

    if (props.open) {
        return <ApiKeyDialog token={token} scopes={scopes} {...props} onSubmit={async (token) => {
            showLoader("Saving...");
            try {
                await api.admin.modifyApiKey(token as ExistingToken);
                success(`API key ${token.name} has been modified.`);
            } finally {
                hideLoader();
            }
            return true;
        }}/>
    } else {
        return null;
    }
}
